/**
 * Client-side SMS helpers (mirrors server/sms/phone-utils.js).
 */

export function normalizePhoneE164(value) {
  if (value == null || value === '') return null;
  let digits = String(value).replace(/\D/g, '');
  if (!digits) return null;

  if (digits.startsWith('61')) {
    digits = digits.slice(2);
  } else if (digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  if (digits.length !== 9 || !digits.startsWith('4')) {
    return null;
  }

  return `+61${digits}`;
}

export function formatPhoneDisplay(e164) {
  if (!e164) return '';
  const normalized = normalizePhoneE164(e164);
  if (!normalized) return String(e164);
  const local = `0${normalized.slice(3)}`;
  return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`;
}

export function countSmsSegments(text) {
  const len = (text || '').length;
  if (len === 0) return 0;
  const singleLimit = 160;
  const multiLimit = 153;
  if (len <= singleLimit) return 1;
  return Math.ceil(len / multiLimit);
}

export function previewSmsBody(body, maxLen = 80) {
  const t = (body || '').replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

export function previewSmsMessage({ body, media }, maxLen = 80) {
  const hasMedia = Array.isArray(media) && media.length > 0;
  const text = (body || '').replace(/\s+/g, ' ').trim();
  if (hasMedia && !text) {
    return media.length > 1 ? `[${media.length} images]` : '[Image]';
  }
  if (hasMedia && text) {
    const prefix = media.length > 1 ? `[${media.length} images] ` : '[Image] ';
    return previewSmsBody(`${prefix}${text}`, maxLen);
  }
  return previewSmsBody(text, maxLen);
}

export function formatSmsTimestamp(value) {
  if (!value) return '-';
  try {
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const time = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    if (isToday) return time;
    return `${date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit' })} ${time}`;
  } catch {
    return '-';
  }
}

/** DD/MM/YYYY h:mm am/pm for tables and file notes. */
export function formatSmsDateTime(value) {
  if (!value) return '-';
  try {
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${day}/${month}/${year} ${time}`;
  } catch {
    return '-';
  }
}

export function getSmsStatusLabel(status) {
  const s = (status || '').toLowerCase();
  if (s === 'delivered' || s === 'received') return 'Delivered';
  if (s === 'sent') return 'Sent';
  if (s === 'failed' || s === 'undelivered') return 'Failed';
  if (s === 'queued' || s === 'accepted' || s === 'sending') return 'Sending';
  return status || '';
}

function resolveTeamLine(item, { isOutbound }) {
  return (
    item?.teamSmsNumber
    || (isOutbound ? item?.from : item?.to)
    || null
  );
}

function formatLinePart(item, { isOutbound }) {
  const line = formatPhoneDisplay(resolveTeamLine(item, { isOutbound }));
  return line ? `Line ${line}` : null;
}

function formatSmsSenderPart(item, isOutbound) {
  if (isOutbound) {
    return item?.sentByName || item?.sentBy || 'Team';
  }
  const clientPhone = formatPhoneDisplay(item?.from);
  return clientPhone ? `Client ${clientPhone}` : 'Client';
}

/** Footer metadata for SMS bubbles: time · line · sender · status */
export function formatSmsBubbleMeta(item) {
  const isOutbound = item?.direction === 'outbound';
  const parts = [formatSmsDateTime(item?.createdAt)];
  const linePart = formatLinePart(item, { isOutbound });
  if (linePart) parts.push(linePart);
  parts.push(formatSmsSenderPart(item, isOutbound));
  const statusLabel = getSmsStatusLabel(item?.status);
  if (statusLabel) parts.push(statusLabel);
  return parts.filter(Boolean).join(' · ');
}

function formatCallHandlerPart(item) {
  if (item?.adviserName) return item.adviserName;
  if (item?.adviserEmail) return item.adviserEmail;
  return null;
}

/** Footer metadata for call bubbles: time · line · handler */
export function formatCallBubbleMeta(item) {
  const parts = [formatSmsDateTime(item?.startedAt || item?.createdAt)];
  const linePart = formatLinePart(item, { isOutbound: item?.direction === 'outbound' });
  if (linePart) parts.push(linePart);
  const handler = formatCallHandlerPart(item);
  if (handler) parts.push(handler);
  return parts.filter(Boolean).join(' · ');
}
